import { playRemoteMedia, type RemotePlayResult } from './remotePlayback';

/**
 * Singleton, DOM-less audio element for the remote call audio.
 *
 * Browsers only allow audible playback started inside a user gesture; on
 * Android WebView / iOS the permission is granted PER ELEMENT. So we "bless"
 * this one element by playing a silent WAV inside the Chiama/Rispondi tap,
 * then route the remote stream through the same element seconds later when
 * WebRTC connects — playback is still allowed.
 */

// 50ms of real zero samples (8kHz mono 16bit), generated once at runtime.
// A zero-SAMPLE wav is not enough: Safari never resolves play() on it, and a
// hanging unlock would freeze the Chiama/Rispondi tap forever.
let silentWavUrl: string | null = null;
function getSilentWavUrl(): string {
  if (silentWavUrl) return silentWavUrl;
  const sampleRate = 8000;
  const samples = 400;
  const buf = new ArrayBuffer(44 + samples * 2);
  const v = new DataView(buf);
  const writeAscii = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) v.setUint8(offset + i, s.charCodeAt(i));
  };
  writeAscii(0, 'RIFF');
  v.setUint32(4, 36 + samples * 2, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true);
  v.setUint32(28, sampleRate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  writeAscii(36, 'data');
  v.setUint32(40, samples * 2, true);
  silentWavUrl = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
  return silentWavUrl;
}

// Whatever a browser does with the unlock clip, the call flow must never
// hang on it: past this deadline we fail open ('unavailable').
const UNLOCK_TIMEOUT_MS = 1500;

let element: HTMLAudioElement | null = null;

function getElement(): HTMLAudioElement {
  if (!element) {
    element = document.createElement('audio');
    element.setAttribute('playsinline', '');
  }
  return element;
}

export type UnlockResult = 'unlocked' | 'blocked' | 'unavailable';

/**
 * Call INSIDE the user gesture (tap on Chiama/Rispondi), BEFORE any signaling.
 * - 'unlocked'    → element blessed, remote audio will play
 * - 'blocked'     → NotAllowedError: playback impossible in this browser
 *                   context (in-app WebView) — warn the user and abort the call
 * - 'unavailable' → anything else (e.g. wav unsupported): fail open, proceed
 */
export async function unlockRemoteAudio(): Promise<UnlockResult> {
  const el = getElement();
  try {
    el.srcObject = null;
    el.src = getSilentWavUrl();
    el.muted = false;
    const playPromise = el.play();
    // If the timeout wins, a late rejection must not become an unhandled one.
    void playPromise.catch(() => {});
    const result = await Promise.race([
      playPromise.then(() => 'unlocked' as const),
      new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), UNLOCK_TIMEOUT_MS)),
    ]);
    return result === 'unlocked' ? 'unlocked' : 'unavailable';
  } catch (err) {
    return (err as DOMException)?.name === 'NotAllowedError' ? 'blocked' : 'unavailable';
  }
}

/** Route the remote stream through the blessed element and start playback. */
export async function attachRemoteAudioStream(stream: MediaStream): Promise<RemotePlayResult> {
  const el = getElement();
  el.removeAttribute('src');
  if (el.srcObject !== stream) el.srcObject = stream;
  el.muted = false;
  return playRemoteMedia(el);
}

/** Stop and release the stream at call end. */
export function detachRemoteAudio(): void {
  if (!element) return;
  element.pause();
  element.srcObject = null;
  element.removeAttribute('src');
}

/**
 * Muta/rimuove il mute dell'elemento audio remoto senza staccare lo stream —
 * usato durante la pausa manuale della consulente (nessun WebRTC teardown,
 * solo silenzio nei due sensi finché dura la pausa).
 */
export function setRemoteAudioMuted(muted: boolean): void {
  if (!element) return;
  element.muted = muted;
}

/** Test hook: inject a fake element (pass null to reset to the real one). */
export function __setAudioElementForTests(el: HTMLAudioElement | null): void {
  element = el;
}
